import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Percent, Calculator, Eye, TrendingUp } from 'lucide-react';
import Disclaimer from '../../components/Disclaimer';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const LireCotes = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Disclaimer />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/guides" data-discover="true" className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux guides
        </Link>

        <article className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Comment Lire et Comprendre les Cotes de Paris
          </h1>
          
          <div className="prose prose-lg max-w-none text-gray-700 space-y-6 leading-relaxed">
            <p className="text-xl text-gray-600 font-medium">
              Les cotes sont le langage universel des paris, mais beaucoup de parieurs ne comprennent pas vraiment ce qu'elles signifient 
              ni comment les utiliser à leur avantage. Maîtriser la lecture des cotes est essentiel pour identifier la valeur, comparer 
              les offres entre sites, et maximiser vos gains potentiels. Ce guide vous explique tout ce que vous devez savoir sur les cotes.
            </p>

            <div className="bg-purple-50 border-l-4 border-purple-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Percent className="w-6 h-6 text-purple-600" />
                Comprendre le Système de Cotes Décimales
              </h2>
              <p>
                En France, les sites de paris utilisent le système décimal (aussi appelé "cote européenne"). Une cote de 2,00 signifie 
                que si vous misez 10€ et que vous gagnez, vous récupérez 20€ (votre mise de 10€ plus 10€ de gain). Pour calculer votre 
                gain potentiel, multipliez simplement votre mise par la cote. Une mise de 50€ sur une cote de 3,50 vous rapporte 175€ 
                au total (50€ x 3,50), soit 125€ de gain net.
              </p>
              <p>
                Plus la cote est élevée, plus le résultat est considéré comme improbable par le site de paris. Une cote de 1,20 indique 
                un favori très probable (vous gagnez 20€ pour 100€ misés), tandis qu'une cote de 10,00 indique un outsider peu probable 
                (vous gagnez 900€ pour 100€ misés). Mais attention : les cotes ne reflètent pas toujours la probabilité réelle, c'est 
                là que se trouve l'opportunité pour les parieurs avisés.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Convertir les Cotes en Probabilités Implicites
            </h2>
            <p>
              Pour identifier la valeur dans les cotes, vous devez comprendre les probabilités implicites. La formule est simple : 
              probabilité implicite = 1 / cote. Une cote de 2,00 implique une probabilité de 50% (1 / 2,00). Une cote de 4,00 implique 
              une probabilité de 25% (1 / 4,00). Mais ces probabilités incluent la marge de la maison de paris, donc elles sont toujours 
              légèrement supérieures à la probabilité réelle.
            </p>
            <p>
              Pour un match avec trois résultats possibles (victoire domicile, nul, victoire extérieur), additionnez les probabilités 
              implicites des trois cotes. Le total dépasse toujours 100% : c'est la marge de la maison. Par exemple, si les trois cotes 
              sont 2,00 / 3,50 / 3,00, les probabilités implicites sont 50% / 28,6% / 33,3% = 111,9%. La marge est donc de 11,9%, 
              ce qui est la marge moyenne du marché français.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calculator className="w-6 h-6 text-blue-600" />
                Identifier les Cotes Sous-Valorisées
              </h2>
              <p>
                Une cote sous-valorisée est une cote qui offre plus de valeur que ce que la probabilité réelle suggère. Si vous estimez 
                qu'une équipe a 40% de chances de gagner (probabilité réelle), mais que la cote correspond à une probabilité implicite 
                de 35%, vous avez trouvé de la valeur. La différence entre votre estimation et la probabilité implicite est votre avantage 
                théorique.
              </p>
              <p>
                Pour trouver ces opportunités, vous devez développer votre propre système d'évaluation des probabilités basé sur 
                l'analyse statistique, la forme récente, les confrontations directes, et d'autres facteurs pertinents. Comparez ensuite 
                vos estimations avec les cotes proposées. Si vos probabilités sont supérieures aux probabilités implicites, vous avez 
                identifié une valeur potentielle.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Comparer les Cotes entre Différents Sites
            </h2>
            <p>
              Les cotes varient entre les sites de paris, parfois de manière significative. Un même match peut avoir des cotes différentes 
              de 0,10 à 0,20 points selon le site. Ces différences peuvent sembler minimes, mais sur le long terme, elles font une 
              différence considérable dans vos gains. Toujours comparer les cotes avant de placer un pari, surtout pour les paris avec 
              des mises importantes.
            </p>
            <p>
              Certains sites sont réputés pour offrir de meilleures cotes sur certains sports ou types de paris. Par exemple, un site peut 
              avoir des cotes supérieures sur le football français mais des cotes inférieures sur le tennis. Connaître les forces de 
              chaque site vous permet d'optimiser vos gains en choisissant toujours le meilleur prix disponible.
            </p>

            <div className="bg-green-50 border-l-4 border-green-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Eye className="w-6 h-6 text-green-600" />
                Comprendre l'Évolution des Cotes
              </h2>
              <p>
                Les cotes ne sont pas fixes : elles évoluent constamment selon les paris placés, les informations disponibles (blessures, 
                changements d'équipe), et les ajustements des algorithmes des sites. Une cote qui baisse indique généralement que beaucoup 
                d'argent est misé sur ce résultat, ce qui peut révéler des informations ou simplement refléter l'opinion publique.
              </p>
              <p>
                Suivre l'évolution des cotes peut vous donner des indices précieux. Si une cote baisse rapidement sans raison évidente, 
                cela peut indiquer des informations non publiques (blessure, changement tactique). Mais attention : parfois, les mouvements 
                de cotes sont simplement dus à des paris importants de gros parieurs, pas nécessairement à une meilleure information. 
                Analysez toujours dans le contexte.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Les Cotes et les Types de Paris
            </h2>
            <p>
              Différents types de paris ont des cotes différentes selon leur complexité et leur probabilité. Un pari simple (1N2) a 
              généralement des cotes plus basses mais plus prévisibles. Un pari combiné (accumulateur) multiplie les cotes mais réduit 
              drastiquement la probabilité de gain. Un pari avec handicap ajuste les cotes pour équilibrer les chances entre deux équipes 
              de niveaux différents.
              </p>
              <p>
                Comprenez comment chaque type de pari affecte les cotes et les probabilités. Un combiné à 3 sélections avec des cotes de 
                2,00 chacune donne une cote totale de 8,00 (2 x 2 x 2), mais la probabilité de gagner les trois est beaucoup plus faible 
                que la probabilité de gagner chaque pari individuellement. Les cotes élevées des combinés sont séduisantes mais souvent 
              trompeuses.
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 my-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-yellow-600" />
                Utiliser les Cotes pour Gérer Votre Bankroll
              </h2>
              <p>
                Vos mises doivent être proportionnelles à la valeur perçue dans les cotes, pas seulement à votre niveau de confiance. 
                Si vous identifiez une très forte valeur (grand écart entre votre estimation et la probabilité implicite), vous pouvez 
                augmenter légèrement votre mise, même si votre confiance absolue n'est pas maximale. À l'inverse, même avec une très haute 
                confiance, si la cote ne présente pas de valeur (votre estimation correspond à la probabilité implicite), limitez votre mise.
              </p>
              <p>
                Cette approche vous permet de maximiser vos gains sur les paris avec la meilleure valeur tout en limitant les risques sur 
                les paris où la valeur est faible. Les professionnels ne parient pas seulement sur ce qu'ils pensent probable, mais sur 
                ce qui offre de la valeur selon les cotes disponibles. C'est la différence fondamentale entre parier pour le plaisir et 
                parier pour gagner à long terme.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">
              Notre Approche Recommandée
            </h2>
            <p>
              Pour maîtriser les cotes, apprenez d'abord à les convertir en probabilités implicites, développez votre propre système 
              d'évaluation des probabilités réelles, comparez toujours les cotes entre différents sites, identifiez les écarts de valeur, 
              et ajustez vos mises selon la valeur perçue plutôt que seulement selon votre confiance. Les cotes sont votre outil principal 
              pour identifier les opportunités : apprenez à les lire correctement et elles vous guideront vers les meilleurs paris.
            </p>
            <p>
              Rappelez-vous : les cotes ne sont pas des prédictions absolues, mais des estimations ajustées par la marge de la maison. 
              Votre objectif n'est pas de trouver les cotes "justes", mais de trouver les cotes qui sous-estiment les probabilités réelles 
              selon votre analyse. C'est dans ces écarts que se trouvent les gains à long terme. Maîtrisez la lecture des cotes, 
              développez votre capacité à identifier la valeur, et vous transformerez les paris d'un jeu de chance en une activité où 
              l'expertise fait la différence.
            </p>
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
};

export default LireCotes;

